/**
 * Web push (PushPort): firebase/messaging over the app's own PWA service worker (sw/sw.ts shows background
 * messages); no separate firebase-messaging-sw.js is ever registered. Not offered in the desktop app (its alerts come
 * from the WebSocket through the Tauri notification plugin), in mock mode, or on dev servers (no service worker);
 * "unconfigured" when the build has no VAPID key.
 */
import type { PushPermission, PushPort } from "@bundle/core";
import { isDesktop } from "./desktop.ts";
import { MOCK } from "./env.ts";
import { firebaseApp } from "./firebase/app.ts";
import { webPushVapidKey } from "./firebase/config.ts";

/**
 * Web push (VAPID) public key: config/firebase/web.json `vapidKey` (Firebase console → Cloud Messaging → Web Push
 * certificates). VITE_FIREBASE_VAPID_KEY overrides it at build time (e.g. a separate Firebase project for staging).
 */
const VAPID_KEY: string | undefined = import.meta.env.VITE_FIREBASE_VAPID_KEY?.trim() || webPushVapidKey || undefined;

async function messaging() {
  const [m, app] = await Promise.all([import("firebase/messaging"), firebaseApp()]);
  if (!(await m.isSupported())) throw new Error("Push messaging isn't supported in this browser");
  return { m, messaging: m.getMessaging(app) };
}

/** The PWA's registration (scope "/"); Firebase must use it instead of registering its default worker. */
async function appRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  return (await navigator.serviceWorker.getRegistration("/")) ?? undefined;
}

export const webPush: PushPort = {
  availability() {
    if (MOCK || isDesktop() || !import.meta.env.PROD) return "unsupported";
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return "unsupported";
    return VAPID_KEY === undefined ? "unconfigured" : "available";
  },

  permission(): PushPermission {
    return "Notification" in window ? Notification.permission : "denied";
  },

  // Called synchronously from the click handler (NotificationsViewModel.enable).
  requestPermission: () => Notification.requestPermission(),

  async token() {
    const registration = await appRegistration();
    if (registration === undefined || VAPID_KEY === undefined) throw new Error("No service worker or VAPID key");
    const { m, messaging: instance } = await messaging();
    return m.getToken(instance, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
  },

  async deleteToken() {
    if (!("serviceWorker" in navigator)) return;
    const registration = await appRegistration();
    if (registration === undefined) return;
    if (Notification.permission === "granted" && VAPID_KEY !== undefined) {
      const { m, messaging: instance } = await messaging();
      // getToken binds Firebase to our worker (returns the stored token); deleteToken then revokes it at FCM.
      await m.getToken(instance, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
      await m.deleteToken(instance);
    } else {
      // Permission withdrawn in the browser: dropping the push subscription is enough (FCM reports it gone).
      await (await registration.pushManager.getSubscription())?.unsubscribe();
    }
  },
};

/** After reading a conversation here: close its notifications in this browser (no "read" push reaches browsers). */
export function closeChannelNotifications(channelId: string): void {
  if (MOCK || !("serviceWorker" in navigator) || !("Notification" in window) || Notification.permission !== "granted") return;
  void appRegistration()
    .then(async (registration) => {
      for (const n of (await registration?.getNotifications({ tag: channelId })) ?? []) n.close();
    })
    .catch(() => undefined);
}
